import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { companiesQueryOptions } from '@/entities/company'
import {
  CustomerVisibilityField,
  customerVisibilityBlock,
  type MeetingDetail,
  MeetingTagsInput,
  projectRequiresCompany,
  type UpdateMeetingInput,
  useUpdateMeetingMutation,
} from '@/entities/meeting'
import { projectsQueryOptions } from '@/entities/project'
import { ApiError } from '@/shared/api'
import { deviceZoneDiffers, isoToWallTime, wallTimeToIso, zoneLabel } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { CompanyProjectFields, type CompanyProjectValue } from '@/shared/ui/company-project-fields'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'

interface EditMeetingDialogProps {
  meeting: MeetingDetail
  onClose: () => void
}

// Подпись зоны у поля переноса: значение поля — настенное время зоны
// организации, то же, что показано во всём интерфейсе.
function rescheduleHint(): string {
  const base = `Время в зоне ${zoneLabel()}`
  return deviceZoneDiffers() ? `${base}, а не вашего устройства` : base
}

// Монтируется по открытию (state инициализируется из meeting на mount).
export function EditMeetingDialog({ meeting, onClose }: EditMeetingDialogProps) {
  const [title, setTitle] = useState(meeting.title)
  const [description, setDescription] = useState(meeting.description ?? '')
  const [tags, setTags] = useState<string[]>(meeting.tags)
  const [customerVisible, setCustomerVisible] = useState(meeting.customerVisible)
  const [companyProject, setCompanyProject] = useState<CompanyProjectValue>({
    companyId: meeting.companyId ?? '',
    projectId: meeting.projectId ?? '',
  })

  // Справочники хаба для пикера: проекты — только выбранной компании.
  const companies = useQuery(companiesQueryOptions())
  const projects = useQuery({
    ...projectsQueryOptions(companyProject.companyId),
    enabled: companyProject.companyId !== '',
  })
  const [scheduledAt, setScheduledAt] = useState(
    meeting.scheduledAt ? isoToWallTime(meeting.scheduledAt) : '',
  )
  const [error, setError] = useState<string>()
  const [cpError, setCpError] = useState<string>()
  const mutation = useUpdateMeetingMutation(meeting.id)

  // Перенос времени имеет смысл только для ещё не начавшейся встречи.
  const canReschedule = meeting.state === 'scheduled' && !!meeting.scheduledAt

  // Пометка «видно заказчику» — право круга встречи (как и приватность), а
  // править встречу может любой сотрудник: показываем поле только тем, кто им
  // распоряжается, иначе отказ прилетал бы после сохранения.
  const canSetCustomerVisibility = meeting.canManageAccess

  // Приватность правится не здесь, а на карточке, поэтому блокировка тумблера
  // считается по сохранённому значению встречи и по выбранной прямо сейчас
  // компании. Снять пометку можно всегда — запрещено только включение.
  const visibilityBlock = meeting.customerVisible
    ? undefined
    : customerVisibilityBlock({ private: meeting.private, companyId: companyProject.companyId })

  function handleSubmit() {
    setCpError(undefined)
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setError('Введите название')
      return
    }
    const patch: UpdateMeetingInput = {}
    if (trimmedTitle !== meeting.title) patch.title = trimmedTitle
    if (description.trim() !== (meeting.description ?? '')) patch.description = description.trim()
    if (tags.join(' ') !== meeting.tags.join(' ')) patch.tags = tags
    const nextCustomerVisible = customerVisible && !visibilityBlock
    if (canSetCustomerVisibility && nextCustomerVisible !== meeting.customerVisible)
      patch.customerVisible = nextCustomerVisible
    if (projectRequiresCompany(companyProject.companyId, companyProject.projectId)) {
      setCpError('Проект нельзя указать без компании')
      return
    }
    // company_project — replace: шлём, только если привязка изменилась (оба пусто = снять).
    if (
      companyProject.companyId !== (meeting.companyId ?? '') ||
      companyProject.projectId !== (meeting.projectId ?? '')
    ) {
      patch.companyProject = companyProject
    }
    if (canReschedule && scheduledAt) {
      // Поле — настенное время зоны организации; момент из него собирается явно.
      const nextIso = wallTimeToIso(scheduledAt)
      if (!nextIso) {
        setError('Некорректное время начала')
        return
      }
      if (nextIso !== new Date(meeting.scheduledAt ?? '').toISOString()) {
        if (new Date(nextIso).getTime() < Date.now()) {
          setError('Время начала уже прошло')
          return
        }
        patch.scheduledAt = nextIso
      }
    }
    if (Object.keys(patch).length === 0) {
      onClose()
      return
    }
    mutation.mutate(patch, {
      onSuccess: () => {
        toast.success('Встреча обновлена')
        onClose()
      },
      onError: (err) => {
        // Бэк — последний арбитр инварианта: 422 PROJECT_REQUIRES_COMPANY на поле.
        if (err instanceof ApiError && err.code === 'PROJECT_REQUIRES_COMPANY') {
          setCpError('Проект нельзя указать без компании')
          return
        }
        toast.error('Не удалось сохранить изменения')
      },
    })
  }

  return (
    <Dialog open onClose={onClose} title="Редактировать встречу">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        className="flex flex-col gap-4"
      >
        <Field label="Название" {...(error ? { error } : {})}>
          {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field label="Описание" hint="Опционально: повестка или контекст встречи">
          {(p) => (
            <Textarea
              {...p}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="О чём встреча"
              maxLength={2000}
            />
          )}
        </Field>
        <Field label="Теги" hint="Тема встречи. Если пусто, встреча внутренняя">
          {(p) => <MeetingTagsInput {...p} value={tags} onChange={setTags} />}
        </Field>
        <CompanyProjectFields
          value={companyProject}
          onChange={setCompanyProject}
          companies={companies.data ?? []}
          projects={projects.data ?? []}
          companiesLoading={companies.isPending}
          projectsLoading={projects.isPending}
          error={cpError}
        />
        {canSetCustomerVisibility && (
          <CustomerVisibilityField
            checked={customerVisible && !visibilityBlock}
            onChange={setCustomerVisible}
            companyName={
              companies.data?.find((c) => c.id === companyProject.companyId)?.name ??
              companyProject.companyId
            }
            {...(visibilityBlock ? { block: visibilityBlock } : {})}
          />
        )}
        {canReschedule && (
          <Field label="Время начала" hint={rescheduleHint()}>
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            )}
          </Field>
        )}
        <div className="mt-2 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
